import { useState } from 'react';
import { ICompositeTextWidget, IParagraphWidget } from '../widgets/types';
import { apiClient } from '../../../utils/apiClient';

// Définition de l'interface pour les résultats d'analyse
interface AnalysisResult {
  originalText: string;
  legalReferences: LegalReference[];
  summary?: string;
}

// Interface pour les références légales
interface LegalReference {
  text: string;
  type: string;
  found: boolean;
  codeId?: string;
  codeTitle?: string;
  sectionId?: string;
  sectionTitle?: string;
  articleId?: string;
  articleNum?: string;
  position?: {
    start: number;
    end: number;
  };
}

export const useSparkleAiAnalysis = (dossierId: string) => {
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);

  /**
   * Calcule la position réelle d'une référence dans le texte original
   * Cherche d'abord le numéro d'article seul, puis le texte complet si pas trouvé
   */
  const findRealPosition = (text: string, ref: any): { start: number; end: number } | null => {
    // Stratégie 1: Chercher le numéro d'article seul (ex: "1103", "L. 142-2")
    if (ref.article) {
      const articleIndex = text.indexOf(ref.article);
      if (articleIndex !== -1) {
        return {
          start: articleIndex,
          end: articleIndex + ref.article.length
        };
      }
    }

    // Stratégie 2: Chercher le texte original complet (fallback)
    const fullTextIndex = text.indexOf(ref.originalText);
    if (fullTextIndex !== -1) {
      return {
        start: fullTextIndex,
        end: fullTextIndex + ref.originalText.length
      };
    }

    // Pas trouvé
    return null;
  };

  /**
   * Analyse le texte fourni en utilisant l'API d'IA
   */
  const analyzeText = async (text: string): Promise<AnalysisResult | null> => {
    setIsAnalyzing(true);

    try {
      const response = await apiClient('/legal-references/extract', {
        method: 'POST',
        body: JSON.stringify({
          text,
          enrichWithLegifrance: true
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        // Transformer le format de réponse du nouvel endpoint vers le format attendu
        const transformedData: AnalysisResult = {
          originalText: text,
          legalReferences: result.data.map((ref: any) => ({
            text: ref.originalText,
            type: ref.type || 'article',
            found: ref.found || false,
            codeId: ref.codeId,
            codeTitle: ref.codeTitle,
            sectionId: ref.sectionId,
            sectionTitle: ref.sectionTitle,
            articleId: ref.articleId,
            articleNum: ref.articleNum || ref.article,
            position: findRealPosition(text, ref) // Calcul côté client avec indexOf
          }))
        };

        setAnalysisResult(transformedData);
        return transformedData;
      } else {
        console.error('Réponse API invalide:', result);
        return null;
      }
    } catch (error) {
      console.error('Erreur lors de l\'analyse du texte:', error);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  /**
   * Vérifie si une référence légale doit être enrichie
   */
  const shouldEnrichReference = (reference: LegalReference): boolean => {
    // Ne pas enrichir les références dont l'ID d'article commence par "JORFA"
    if (reference.articleId && reference.articleId.startsWith('JORFA')) {
      return false;
    }

    // Ne pas enrichir les références qui n'ont pas été trouvées
    if (!reference.found) {
      return false;
    }

    return true;
  }

  /**
   * Applique les résultats de l'analyse à un bloc de texte composite
   * Utilise une approche par tokens pour éviter toute corruption HTML
   */
  const applyAnalysisToCompositeBlock = (
    compositeBlock: ICompositeTextWidget | IParagraphWidget,
    analysisResult: AnalysisResult
  ): ICompositeTextWidget | IParagraphWidget => {
    if (!analysisResult || !analysisResult.legalReferences || analysisResult.legalReferences.length === 0) {
      return compositeBlock;
    }


    // Création d'une copie profonde du bloc composite
    const updatedBlock = JSON.parse(JSON.stringify(compositeBlock)) as ICompositeTextWidget | IParagraphWidget;

    // Filtrer uniquement les références valides avec position
    const validReferences = analysisResult.legalReferences
      .filter(ref => ref.found && ref.position && shouldEnrichReference(ref))
      .sort((a, b) => (a.position?.start || 0) - (b.position?.start || 0));

    if (validReferences.length === 0) {
      return updatedBlock;
    }

    // Calculer les offsets de chaque bloc dans le texte complet
    const blockOffsets: number[] = [];
    let currentOffset = 0;

    for (const block of updatedBlock.content.blocks) {
      blockOffsets.push(currentOffset);
      currentOffset += block.rawText.length + 1; // +1 pour compenser le saut de ligne entre blocs
    }

    // Traiter chaque bloc individuellement
    for (let blockIndex = 0; blockIndex < updatedBlock.content.blocks.length; blockIndex++) {
      const block = updatedBlock.content.blocks[blockIndex];
      const blockStart = blockOffsets[blockIndex];
      const blockEnd = blockIndex + 1 < blockOffsets.length
        ? blockOffsets[blockIndex + 1] - 1
        : blockStart + block.rawText.length;

      // Filtrer les références qui appartiennent à ce bloc
      const blockReferences = validReferences.filter(ref => {
        const refStart = ref.position?.start || 0;
        return refStart >= blockStart && refStart < blockEnd;
      });

      if (blockReferences.length === 0) {
        continue;
      }

      interface Segment {
        type: 'text' | 'reference';
        content: string;
        reference?: LegalReference;
      }

      const splitPoints = new Set<number>();
      splitPoints.add(0);
      splitPoints.add(block.rawText.length);

      for (const ref of blockReferences) {
        if (!ref.position) continue;

        const localStart = ref.position.start - blockStart;
        const localEnd = ref.position.end - blockStart;

        if (localStart >= 0 && localStart <= block.rawText.length) {
          splitPoints.add(localStart);
        }

        if (localEnd >= 0 && localEnd <= block.rawText.length) {
          splitPoints.add(localEnd);
        }
      }

      const sortedSplitPoints = Array.from(splitPoints).sort((a, b) => a - b);

      const segments: Segment[] = [];

      for (let i = 0; i < sortedSplitPoints.length - 1; i++) {
        const start = sortedSplitPoints[i];
        const end = sortedSplitPoints[i + 1];

        if (start === end) continue;

        const segmentText = block.rawText.substring(start, end);

        let isReference = false;
        let referenceData: LegalReference | undefined;

        for (const ref of blockReferences) {
          if (!ref.position) continue;

          const localStart = ref.position.start - blockStart;
          const localEnd = ref.position.end - blockStart;

          if (start >= localStart && end <= localEnd) {
            isReference = true;
            referenceData = ref;
            break;
          }
        }

        segments.push({
          type: isReference ? 'reference' : 'text',
          content: segmentText,
          reference: referenceData
        });
      }

      let enrichedText = '';

      segments.forEach((segment, index) => {
        if (segment.type === 'text') {
          enrichedText += segment.content;
        } else if (segment.type === 'reference' && segment.reference) {
          const ref = segment.reference;

          if (!shouldEnrichReference(ref)) {
            enrichedText += segment.content;
            return;
          }

          const attributes = [];

          attributes.push('nature="code"');

          if (ref.codeId) {
            attributes.push(`data-legifrance-code="${ref.codeId}"`);
          }

          if (ref.sectionId) {
            attributes.push(`data-legifrance-section="${ref.sectionId}"`);
          }

          if (ref.type.toLowerCase() === 'article' && ref.articleId) {
            attributes.push(`data-legifrance-article="${ref.articleId}"`);
          }

          const startTag = `<span class="legifrance-highlight" ${attributes.join(' ')}>`;
          const endTag = '</span>';

          enrichedText += startTag + segment.content + endTag;
        }
      });

      updatedBlock.content.blocks[blockIndex].rawText = enrichedText;
    }

    return updatedBlock;
  };

  return {
    isAnalyzing,
    analyzeText,
    applyAnalysisToCompositeBlock,
    analysisResult
  };
};
